/**
 * Banner for data that is ON SCREEN but could not be refreshed because the
 * server was unreachable (ISS-445). The companion of DegradedReadState:
 * that one owns "failed with nothing to show", this one owns "showing
 * something, but it may be out of date".
 *
 * A transient connectivity error must never blank data the user already has —
 * render the data, say it is stale, offer a refresh.
 */
import { CloudOff } from 'lucide-react';
import { Button } from '../forms/Button';

interface StaleDataBannerProps {
  /** Re-run the failed refresh (typically the query's `refetch`). */
  onRetry: () => void;
  /** True while the refresh is in flight. */
  isRetrying?: boolean;
}

export function StaleDataBanner({ onRetry, isRetrying = false }: StaleDataBannerProps) {
  return (
    <div
      role="status"
      className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-amber-800 text-sm mb-4 flex items-center gap-2.5"
    >
      <CloudOff className="w-4 h-4 shrink-0" aria-hidden="true" />
      <span className="flex-1">
        Weak connection — showing previously loaded data, which may be out of date.
      </span>
      <Button
        variant="warning"
        size="sm"
        onClick={onRetry}
        loading={isRetrying}
        loadingText="Refreshing..."
      >
        Refresh
      </Button>
    </div>
  );
}
